import { UserManager, WebStorageStateStore } from 'oidc-client-ts';
import { ApplicationPaths, ApplicationName } from './ApiAuthorizationConstants';

export class AuthorizeService {
  _callbacks: any[] = [];
  _nextSubscriptionId = 0;
  _user: any = null;
  _isAuthenticated = false;

  // By default pop ups are disabled because they don't work properly on Edge.
  // If you want to enable pop up authentication simply set this flag to false.
  _popUpDisabled = true;

  userManager: UserManager | null = null;

  async isAccessExpired() {
    if (this._user?.expired) {
      return true;
    }

    await this.ensureUserManagerInitialized();
    const user = await this.userManager?.getUser();
    return user && user.expired;
  }

  async isAuthenticated() {
    const user = await this.getUser();
    return !!user;
  }

  async getUser() {
    if (this._user?.profile) {
      return this._user.profile;
    }

    await this.ensureUserManagerInitialized();
    const user = await this.userManager?.getUser();
    return user && user.profile;
  }

  async getAccessToken() {
    await this.ensureUserManagerInitialized();
    const user = await this.userManager?.getUser();
    return user && user.access_token;
  }

  // We try to authenticate the user in three different ways:
  // 1) We try to see if we can authenticate the user silently. This happens
  //    when the user is already logged in on the IdP and is done using a hidden iframe
  //    on the client.
  // 2) We try to authenticate the user using a PopUp Window. This might fail if there is a
  //    Pop-Up blocker or the user has disabled PopUps.
  // 3) If the two methods above fail, we redirect the browser to the IdP to perform a traditional
  //    redirect flow.
  async signIn(urlToRedirect: string) {
    await this.ensureUserManagerInitialized();
    try {
      const silentUser = await this.userManager?.signinSilent(this.createArguments(undefined));
      this.updateState(silentUser);
      return this.success(urlToRedirect);
    } catch (silentError) {
      // User might not be authenticated, fallback to popup authentication
      console.log('Silent authentication error: ', silentError);

      try {
        if (this._popUpDisabled) {
          throw new Error('Popup authorization disabled.');
        }

        const popUpUser = await this.userManager?.signinPopup(this.createArguments(undefined));
        this.updateState(popUpUser);
        return this.success(urlToRedirect);
      } catch (popUpError) {
        if (popUpError instanceof Error) {
          if (popUpError.message === 'Popup window closed') {
            // The user explicitly cancelled the login action by closing an opened popup.
            return this.error('The user closed the window.');
          } else if (!this._popUpDisabled) {
            console.log('Popup authentication error: ', popUpError);
          }
        }

        // PopUps might be blocked by the user, fallback to redirect
        try {
          await this.userManager?.signinRedirect(this.createArguments(urlToRedirect));
          return this.redirect();
        } catch (redirectError) {
          console.log('Redirect authentication error: ', redirectError);
          if (redirectError instanceof Error) {
            return this.error(redirectError.message);
          } else {
            return this.error('There was an unknown redirect authentication error');
          }
        }
      }
    }
  }

  async completeSignIn(url: string) {
    try {
      await this.ensureUserManagerInitialized();
      const user = await this.userManager?.signinCallback(url);
      this.updateState(user);
      return this.success((user && (user.state as string)) ?? '');
    } catch (error) {
      console.log('There was an error signing in: ', error);
      return this.error('There was an error signing in.');
    }
  }

  // We try to sign out the user in two different ways:
  // 1) We try to do a sign-out using a PopUp Window. This might fail if there is a
  //    Pop-Up blocker or the user has disabled PopUps.
  // 2) If the method above fails, we redirect the browser to the IdP to perform a traditional
  //    post logout redirect flow.
  async signOut(state: any) {
    await this.ensureUserManagerInitialized();
    try {
      if (this._popUpDisabled) {
        throw new Error('Popup authorization disabled.');
      }

      await this.userManager?.signoutPopup(this.createArguments(undefined));
      this.updateState(undefined);
      return this.success(state);
    } catch (popupSignOutError) {
      console.log('Popup signout error: ', popupSignOutError);
      try {
        await this.userManager?.signoutRedirect(this.createArguments(state));
        return this.redirect();
      } catch (redirectSignOutError) {
        console.log('Redirect signout error: ', redirectSignOutError);
        if (redirectSignOutError instanceof Error) {
          return this.error(redirectSignOutError.message);
        } else {
          return this.error('There was an unknown redirect signout error');
        }
      }
    }
  }

  async completeSignOut(url: string) {
    await this.ensureUserManagerInitialized();
    try {
      const response = await this.userManager?.signoutCallback(url);
      this.updateState(null);
      return this.success((response && (response as any).data) ?? '');
    } catch (error) {
      console.log(`There was an error trying to log out '${error}'.`);
      if (error instanceof Error) {
        return this.error(error.message);
      } else {
        return this.error('There was an unknown error trying to log out');
      }
    }
  }

  updateState(user: any) {
    this._user = user;
    this._isAuthenticated = !!this._user;
    this.notifySubscribers();
  }

  subscribe(callback: any) {
    this._callbacks.push({
      callback,
      subscription: this._nextSubscriptionId++,
    });
    return this._nextSubscriptionId - 1;
  }

  unsubscribe(subscriptionId: number) {
    const subscriptionIndex = this._callbacks
      .map((element, index) => (element.subscription === subscriptionId ? { found: true, index } : { found: false }))
      .filter((element) => element.found === true);
    if (subscriptionIndex.length !== 1) {
      throw new Error(`Found an invalid number of subscriptions ${subscriptionIndex.length}`);
    }

    this._callbacks.splice(subscriptionIndex[0].index as number, 1);
  }

  notifySubscribers() {
    for (let i = 0; i < this._callbacks.length; i++) {
      const callback = this._callbacks[i].callback;
      callback();
    }
  }

  createArguments(urlToRedirect: any) {
    return {
      useReplaceToNavigate: true,
      extraQueryParams: {
        urlToRedirect,
      },
    };
  }

  error(message: string) {
    return {
      status: AuthenticationResultStatus.Fail,
      state: undefined,
      message,
    };
  }

  success(urlToRedirect: string) {
    return {
      status: AuthenticationResultStatus.Success,
      state: {
        returnUrl: urlToRedirect,
      },
      message: undefined,
    };
  }

  redirect() {
    return {
      status: AuthenticationResultStatus.Redirect,
      state: undefined,
      message: undefined,
    };
  }

  async ensureUserManagerInitialized() {
    if (this.userManager !== undefined && this.userManager !== null) {
      return;
    }

    const response = await fetch(ApplicationPaths.ApiAuthorizationClientConfigurationUrl);
    if (!response.ok) {
      throw new Error(`Could not load settings for '${ApplicationName}'`);
    }

    const settings = await response.json();
    settings.automaticSilentRenew = true;
    settings.includeIdTokenInSilentRenew = true;
    settings.loadUserInfo = true;
    settings.userStore = new WebStorageStateStore({
      prefix: ApplicationName,
    });
    this.userManager = new UserManager(settings);

    this.userManager.events.addUserSignedOut(async () => {
      await this.userManager?.removeUser();
      this.updateState(undefined);
    });
  }

  static get instance() {
    return authService;
  }
}

const authService = new AuthorizeService();

export default authService;

export const AuthenticationResultStatus = {
  Redirect: 'redirect',
  Success: 'success',
  Fail: 'fail',
};
